<template>
  <svg :id="'issuance-chart-'+id" viewBox="0,0,1000,300" preserveAspectRatio="xMaxYMax meet"></svg>
</template>

<script>
  import * as d3 from 'd3'
  import * as roll from '@/components/mixins/roll'
  import { sprintSmallPercent } from '@/components/mixins/util'

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  function issuance(data) {
    let out = []

    for (let i = 0; i < data.length; i++) {
      let day = roll.itemize(data[i]), acc = 0
      for (let j = 0; j < day.length; j++) {
        out.push({
          day:     i,
          name:    day[j].name,
          count:   day[j].count,
          percent: day[j].percent,
          acc:     acc,
        })
        acc += day[j].count
      }
    }

    return out
  }

  export default {
    name: 'IssuanceGraph',
    props: {
      id: {type: String, required: true},
      data: {type: Object, required: true},
    },
    data: function() {
      return {
        color: d3.scaleOrdinal(['#98abc5', '#8a89a6', '#7b6888', '#6b486b', '#a05d56', '#d0743c', '#ff8c00']),
      }
    },
    methods: {
      draw: function() {
        let color = this.color,
          now = this.data.Now,
          data = issuance(this.data.Days)

        let yMax = d3.max(data, function(d) { return d.acc+d.count })

        let x = d3.scaleLinear().domain([0, 30]).range([55, 995])
        let y = d3.scaleLinear().domain([0, yMax]).range([295, 5])

        let barWidth = (995-55) / 30

        // Start drawing.
        let svg = d3.select('svg#issuance-chart-'+this.id)
        svg.selectAll('*').remove()

        let g = svg.append('g').attr('transform', 'translate(0, 0)')

        // Draw a tooltip for detailed information about a specific block.
        let tooltipg = svg.append('g').attr('visibility', 'hidden')

        let tooltipRect1 = tooltipg.append('rect')
          .attr('x', -6).attr('fill', '#e5e5e5')
        let tooltipRect2 = tooltipg.append('rect')
          .attr('x', -5).attr('fill', 'white')

        let tooltip1 = tooltipg.append('text')
          .attr('font-weight', 'bold').text('Date')
        let tooltip2 = tooltipg.append('text')
          .attr('dx', '1.25em').attr('dy', '1.25em').text('Authority Name')
        let tooltip3 = tooltipg.append('text')
          .attr('dx', '1.25em').attr('dy', '2.5em')

        let bbox1 = tooltip1._groups[0][0].getBBox()
        tooltipRect1.attr('y', bbox1.y-6)
        tooltipRect2.attr('y', bbox1.y-5)
        let bbox2 = tooltip2._groups[0][0].getBBox(),
          swatchY = bbox2.y + bbox2.height/2 - 7
        tooltipg.append('rect')
          .attr('x', 0).attr('y', swatchY).attr('width', 14).attr('height', 14)
          .attr('fill', '#e5e5e5')
        let tooltipSwatch = tooltipg.append('rect')
          .attr('x', 1).attr('y', swatchY+1).attr('width', 12).attr('height', 12)

        // Draw a histogram, with a bar for each day, split up by certificate
        // authority.
        let bar = g.selectAll('.bar')
          .data(data)
          .enter().append('g')
            .attr('class', 'bar')
            .attr('transform', function(d) { return 'translate('+x(d.day)+', '+y(d.acc+d.count)+')'})
            .on('mouseenter', function(d) {
              let t = new Date(now - (30 - d.day)*86400*1000)
              tooltip1.text(months[t.getUTCMonth()] + ' ' + t.getUTCDate())
              tooltip2.text(d.name + ' (' + sprintSmallPercent(d.percent) + ')')
              tooltip3.text(d.count.toLocaleString())
              tooltipSwatch.attr('fill', color(d.name))

              let bbox2 = tooltip2._groups[0][0].getBBox(),
                bbox3 = tooltip3._groups[0][0].getBBox()
              tooltipRect1.attr('width', bbox2.x+bbox2.width+12).attr('height', bbox3.y+bbox3.height-bbox1.y+12)
              tooltipRect2.attr('width', bbox2.x+bbox2.width+10).attr('height', bbox3.y+bbox3.height-bbox1.y+10)

              let xPos = x(d.day)+barWidth+10,
                yPos = (y(d.acc+d.count)+y(d.acc))/2

              let rightSide = xPos + bbox2.x+bbox2.width+12
              if (rightSide > 1000) {
                xPos = x(d.day) - (bbox2.x+bbox2.width+12)
              }
              let bottomSide = yPos + bbox3.y+bbox3.height+12
              if (bottomSide > 295) {
                yPos = 295 - (bbox3.y+bbox3.height+5)
              }
              let topSide = yPos + bbox1.y
              if (topSide < 0) {
                yPos = 10-bbox1.y
              }

              tooltipg.attr('transform', 'translate('+xPos+', '+yPos+')')
                .attr('visibility', 'visible')
            })
            .on('mouseleave', function(d) { tooltipg.attr('visibility', 'hidden') })
        bar.append('rect') // Dark gray rect, gives illusion of 1px border.
          .attr('x', function(d) {return (d.day == 0 ? 0 : -1) })
          .attr('width', barWidth)
          .attr('height', function(d) { return 295-y(d.count) })
          .attr('fill', '#e5e5e5')
        bar.append('rect') // Inner rect, colored for the specific CA.
          .attr('x', function(d) { return (d.day == 0 ? 1 : 0) })
          .attr('y', 1)
          .attr('width', barWidth-1)
          .attr('height', function(d) { return 295-y(d.count) })
          .attr('fill', function(d) { return color(d.name) })

        // Draw the legend for the x-axis (y-axis omitted because its not useful).
        g.append('g')
          .style('font-family', '\'Open Sans\', sans-serif')
          .style('font-size', '0.9em')
          .attr('transform', 'translate(55, 0)')
          .call(d3.axisLeft(y).ticks(5, 's'))
      },
    },

    watch: {
      data: function() { this.draw() },
    },
    mounted: function() { this.draw() },
  }
</script>

<style scoped>
  svg {
    font-size: 0.8rem;

    /* Prevent browser from asking to save/copy image. */
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/widgets/IssuanceGraph.vue